/**
 * ProductListPage (S1.4) — rebuilt on the house list stack.
 *
 * useProductsTable (useAntdTable + pre-first-fetch guard) + productStore
 * filters + useListPage state triad + GenericFilterPopover. Columns per the
 * holistic plan: SKU (bold, highlighted, Internal Product ID secondary) /
 * Display Name / Category path tag / Readiness StatusTag (產品就緒·未就緒 with
 * gap tooltip) / SCN chip / NetSuite Item ID (secondary) — NO qty/ops columns
 * (direct-ship MVP, vocabulary gate). Whole-row nav to the detail page.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Badge, Button, Dropdown, Input, Table, Tag, Tooltip, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import StatusTag from '@/components/common/StatusTag';
import PurpleTag from '@/components/common/PurpleTag';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type {
  CascaderOption,
  FieldConfig,
  FilterValues,
} from '@/components/common/GenericFilterPopover';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { HighlightText } from '@/components/common/HighlightText';
import { useFeature } from '@/features/flags';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import { useProductListMutations, useProductsTable } from '@/hooks/useProducts';
import { buildBreadcrumb, useTaxonomyTree } from '@/hooks/useTaxonomy';
import { useProductStore } from '@/stores/productStore';
import type { ProductFilters } from '@/stores/productStore';
import type { Product, ProductReadinessGap } from '@/types/api/product';
import type { ProductCategory } from '@/types/api/productCategory';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';

const { Text } = Typography;

/** Popover-local filter value shape for the product list. */
interface ProductFilterPopoverValues extends FilterValues {
  category: string[][];
  readiness?: string;
  scn?: string;
  status?: string;
}

const GAP_LABEL_KEYS: Record<ProductReadinessGap, string> = {
  descriptions: 'products:wizard.reviewMeter.groups.descriptions',
  pricingRef: 'products:wizard.reviewMeter.groups.pricingRef',
  costingRef: 'products:wizard.reviewMeter.groups.costingRef',
};

/** Active-node category tree → cascader options (read view — all depths). */
function buildCategoryOptions(nodes: ProductCategory[], isZh: boolean): CascaderOption[] {
  const build = (parentId: string | null): CascaderOption[] =>
    nodes
      .filter((n) => n.parentId === parentId && n.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((n) => {
        const children = build(n.id);
        return {
          value: n.id,
          label: isZh ? n.nameZh : n.nameEn,
          children: children.length > 0 ? children : undefined,
          isLeaf: children.length === 0,
        };
      });
  return build(null);
}

const ProductListPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const isZh = i18n.language.startsWith('zh');

  const {
    filters,
    setSearch,
    setCategoryPaths,
    setReadiness,
    setScnLink,
    setIsActive,
    resetFilters,
  } = useProductStore();

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const toggleStatusDialog = useConfirmationDialog<Product>();
  const [toggling, setToggling] = useState(false);

  const { nodes } = useTaxonomyTree();
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  // Badge parity: category one per selected path; readiness/scn count when off
  // 'all'; isActive counts only when it deviates from the Active default.
  const customFilterCounters = useMemo(
    () => ({
      categoryPaths: (value: unknown) => (Array.isArray(value) ? value.length : 0),
      readiness: (value: unknown) => (value === 'all' ? 0 : 1),
      scnLink: (value: unknown) => (value === 'all' ? 0 : 1),
      isActive: (value: unknown) => (value === true ? 0 : 1),
    }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    ProductFilters,
    Product
  >({
    useTableHook: useProductsTable as (filters: ProductFilters) => TableHookResult<Product>,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  const categoryOptions = useMemo(() => buildCategoryOptions(nodes, isZh), [nodes, isZh]);

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'category',
        label: t('products:fields.category'),
        type: 'cascader',
        options: categoryOptions,
        placeholder: t('products:filters.selectCategory'),
        noDataText: t('products:filters.noCategories'),
        noSearchResultText: t('products:states.noResults'),
        resetText: t('common:actions.reset'),
      },
      {
        key: 'readiness',
        label: t('products:filters.readiness'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'ready', label: t('products:readiness.ready') },
          { value: 'not_ready', label: t('products:readiness.blocked') },
          { value: 'all', label: t('products:filters.allReadiness') },
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'scn',
        label: t('products:filters.scn'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'linked', label: t('products:filters.scnLinked') },
          { value: 'unlinked', label: t('products:filters.scnUnlinked') },
          { value: 'all', label: t('products:filters.scnAll') },
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'status',
        label: t('products:filters.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'active', label: t('common:status.active') },
          { value: 'inactive', label: t('common:status.inactive') },
          { value: 'all', label: t('products:filters.allStatuses') },
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t, categoryOptions]
  );

  const initialPopoverFilters: ProductFilterPopoverValues = useMemo(
    () => ({
      category: filters.categoryPaths,
      readiness: filters.readiness,
      scn: filters.scnLink,
      status:
        filters.isActive === true ? 'active' : filters.isActive === false ? 'inactive' : 'all',
    }),
    [filters.categoryPaths, filters.readiness, filters.scnLink, filters.isActive]
  );

  const handleApplyFilters = (values: ProductFilterPopoverValues) => {
    setCategoryPaths((values.category || []).map((path) => path.map(String)));
    setReadiness(
      values.readiness === 'ready' || values.readiness === 'not_ready' ? values.readiness : 'all'
    );
    setScnLink(values.scn === 'linked' || values.scn === 'unlinked' ? values.scn : 'all');
    if (values.status === 'active') setIsActive(true);
    else if (values.status === 'inactive') setIsActive(false);
    else setIsActive(null);
  };

  const { toggleActive } = useProductListMutations();

  const handleToggleStatus = async () => {
    const product = toggleStatusDialog.data;
    if (!product) return;
    setToggling(true);
    try {
      const wasActive = product.isActive;
      await toggleActive(product);
      message.success(
        t(wasActive ? 'products:messages.deactivated' : 'products:messages.activated')
      );
      refresh();
    } catch {
      message.error(t('common:messages.updateFailed'));
    } finally {
      setToggling(false);
      toggleStatusDialog.close();
    }
  };

  // W9 (V3, flag-gated) — hover/keyboard-focus tooltip carries the MANUAL
  // standard price only; no at-rest price column in v1 (the at-rest home is
  // the Sales & Pricing tab, so the tooltip is a shortcut, never the only
  // carrier). tabIndex keeps it reachable for keyboard/iPad operators.
  const priceHoverEnabled = useFeature('engines.pricing');

  const columns: TableProps<Product>['columns'] = [
    {
      title: t('products:fields.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 200,
      render: (_: string, record) => {
        const showPriceTip = priceHoverEnabled && record.basePrice != null;
        const cell = (
          <div className="tw-flex tw-flex-col tw-gap-0.5" tabIndex={showPriceTip ? 0 : undefined}>
            <span style={{ fontWeight: token.fontWeightStrong }}>
              <HighlightText text={record.sku} searchTerm={filters.search} fallback="-" />
            </span>
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              <HighlightText
                text={record.internalProductId}
                searchTerm={filters.search}
                fallback=""
              />
            </span>
          </div>
        );
        return showPriceTip ? (
          <Tooltip
            trigger={['hover', 'focus']}
            title={[
              `${t('products:fields.basePrice')}:`,
              record.currency,
              record.basePrice?.toFixed(2),
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {cell}
          </Tooltip>
        ) : (
          cell
        );
      },
    },
    {
      title: t('products:fields.displayName'),
      dataIndex: 'displayName',
      key: 'displayName',
      width: 260,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.displayName} searchTerm={filters.search} fallback="-" />
          </span>
          {record.nameZh && (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              <HighlightText text={record.nameZh} searchTerm={filters.search} fallback="" />
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('products:fields.category'),
      dataIndex: 'categoryId',
      key: 'category',
      width: 220,
      render: (categoryId: string) => {
        const node = nodeById.get(categoryId);
        if (!node) return renderDash(undefined);
        const breadcrumb = buildBreadcrumb(nodes, categoryId, isZh ? 'zh' : 'en');
        return (
          <Tooltip title={breadcrumb}>
            <Tag
              bordered={false}
              style={{
                backgroundColor: token.colorFillQuaternary,
                color: token.colorTextSecondary,
                borderRadius: radiusTokens.control,
                margin: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {isZh ? node.nameZh : node.nameEn}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('products:filters.readiness'),
      dataIndex: 'readiness',
      key: 'readiness',
      width: 140,
      render: (_: unknown, record) => {
        const ready = record.readiness?.ready ?? false;
        const tag = (
          <StatusTag
            status={ready ? 'active' : 'pending'}
            label={t(ready ? 'products:readiness.ready' : 'products:readiness.blocked')}
          />
        );
        if (ready) return tag;
        const gaps = (record.readiness?.missing ?? [])
          .map((gap) => t(GAP_LABEL_KEYS[gap]))
          .join(' · ');
        return <Tooltip title={gaps}>{tag}</Tooltip>;
      },
    },
    {
      title: t('products:fields.scn'),
      dataIndex: 'scn',
      key: 'scn',
      width: 130,
      render: (scn: string | null | undefined) =>
        scn ? <PurpleTag>{scn}</PurpleTag> : renderDash(undefined),
    },
    {
      title: t('products:fields.netSuiteItemId'),
      dataIndex: 'netSuiteItemId',
      key: 'netSuiteItemId',
      width: 160,
      render: (value: string | null | undefined) =>
        value ? (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {value}
          </Text>
        ) : (
          renderDash(undefined)
        ),
    },
    {
      title: t('products:fields.action'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: t('common:actions.edit'),
                icon: <EditOutlined />,
                disabled: !record.isActive,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate(`/auth/products/${record.id}/edit`);
                },
              },
              { type: 'divider' },
              {
                key: 'toggle-status',
                label: record.isActive ? t('products:deactivate') : t('products:activate'),
                icon: record.isActive ? <StopOutlined /> : <CheckCircleOutlined />,
                danger: record.isActive,
                style: !record.isActive ? { color: token.colorSuccess } : undefined,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  toggleStatusDialog.open(record);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('products:actionsFor', { name: record.displayName })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('products:list.title')}
        </h1>
      </div>

      {/* Search + filter + create toolbar */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('products:searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<ProductFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={handleApplyFilters}
          initialFilters={initialPopoverFilters}
          fields={filterFields}
          applyText={t('products:filters.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('products:filters.title')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => navigate('/auth/products/add')}
          style={{
            marginLeft: 'auto',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
            paddingLeft: token.paddingLG,
            paddingRight: token.paddingLG,
          }}
        >
          {t('products:add')}
        </Button>
      </div>

      {/* Table — S0.3 state triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('products:states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<Product>
          {...tableProps}
          columns={columns}
          rowKey="id"
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => navigate(`/auth/products/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('products:states.noResults')}
                actionLabel={t('products:states.clearFilters')}
                onAction={resetFilters}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('products:states.noProducts')}
                actionLabel={t('products:states.createFirst')}
                onAction={() => navigate('/auth/products/add')}
                actionIcon={<PlusOutlined />}
              />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1200 }}
        />
      </TableContainer>

      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={handleToggleStatus}
        title={
          toggleStatusDialog.data?.isActive
            ? t('products:deactivateConfirm')
            : t('products:activateConfirm')
        }
        message={
          toggleStatusDialog.data?.isActive
            ? t('products:deactivateDetail', { name: toggleStatusDialog.data?.displayName ?? '' })
            : t('products:activateDetail', { name: toggleStatusDialog.data?.displayName ?? '' })
        }
        type={toggleStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={
          toggleStatusDialog.data?.isActive ? t('products:deactivate') : t('products:activate')
        }
        loading={toggling}
      />
    </div>
  );
};

export default ProductListPage;
